/**
 * What a user can do about an upload that stopped, and the wording each action
 * is offered under.
 *
 * The transport decides which actions an outcome carries, because only it knows
 * which server state the upload is in; the manager and the tile only render and
 * dispatch them. That keeps the choice next to the rule it comes from and keeps
 * `upload-store` free of any knowledge of transport internals.
 */
export enum UploadRecoveryAction {
	/** Abandon this attempt and upload the file again from zero. */
	START_OVER = 'start-over',
	/** Continue the same attempt — the server session is intact and still holds what was transferred. */
	RETRY = 'retry',
	/** Ask the server once more whether finalization finished, without re-uploading anything. */
	RETRY_STATUS_CHECK = 'retry-status-check',
	/** Stop watching. Deliberately leaves the server session and its local resume metadata alone. */
	CLOSE = 'close',
	/** Release the server-side session that is blocking this file, then upload it again. */
	DISCARD = 'discard',
	/** Release the server-side session and give up on this file. */
	CANCEL = 'cancel',
}

/** The label each action is offered under. Fixed here so two surfaces cannot word the same action differently. */
export const UPLOAD_RECOVERY_LABELS: Record<UploadRecoveryAction, string> = {
	[UploadRecoveryAction.START_OVER]: 'Start over',
	[UploadRecoveryAction.RETRY]: 'Retry',
	[UploadRecoveryAction.RETRY_STATUS_CHECK]: 'Retry status check',
	[UploadRecoveryAction.CLOSE]: 'Close',
	[UploadRecoveryAction.DISCARD]: 'Discard',
	[UploadRecoveryAction.CANCEL]: 'Cancel',
}

/**
 * A stopped upload's recovery offer.
 *
 * `releaseSession` is an opaque thunk supplied by the transport: it is how
 * `Discard` and `Cancel` close a server-side session without the manager ever
 * learning that such a thing exists. An outcome that owns no server state
 * carries none.
 */
export interface UploadRecovery {
	actions: UploadRecoveryAction[]
	/**
	 * Whether this outcome ended the upload.
	 *
	 * A stopped upload is not necessarily a failed one: a finalization that
	 * outlasted its automatic observation window is still running on the server,
	 * and reporting it as an error would be untrue as well as alarming. The
	 * surface reads this to decide whether the outcome is presented as a failure
	 * or as something still in progress that is no longer being watched.
	 */
	terminal: boolean
	releaseSession?: () => Promise<void>
}
